import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, Heart, Sparkles, Copy, CheckCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { registerUser, fulfillStripePayment } from '../services/api';

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [fulfillMessage, setFulfillMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const fulfillmentProcessedRef = useRef(false); // Track if fulfillment has been processed

  useEffect(() => {
    // Check for Stripe session_id in URL (from Stripe redirect)
    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      // Prevent duplicate calls - check if already processed
      if (fulfillmentProcessedRef.current) {
        console.log('Fulfillment already processed, skipping duplicate call');
        return;
      }

      // Check if this session was already processed (stored in localStorage)
      const processedSessions = JSON.parse(localStorage.getItem('contemplate_processed_sessions') || '[]');
      if (processedSessions.includes(sessionId)) {
        console.log('Session already processed, loading from localStorage:', sessionId);
        // Load existing data from localStorage
        const affiliationCode = localStorage.getItem('contemplate_affiliation_code');
        const message = localStorage.getItem('contemplate_fulfill_message');
        if (affiliationCode) {
          setRegistrationCode(affiliationCode);
          setRegistrationSuccess(true);
          if (message) {
            setFulfillMessage(message);
          }
        }
        return;
      }

      // Mark as processing to prevent duplicate calls
      fulfillmentProcessedRef.current = true;

      // Handle Stripe payment fulfillment
      console.log('Stripe session_id found, fulfilling payment:', sessionId);
      fulfillStripePayment(sessionId)
        .then(result => {
          if (result.success) {
            console.log('Stripe payment fulfilled successfully:', result);

            // Mark this session as processed
            const updatedSessions = [...processedSessions, sessionId];
            localStorage.setItem('contemplate_processed_sessions', JSON.stringify(updatedSessions));

            // Store order info
            localStorage.setItem('contemplate_order', JSON.stringify({
              sessionId: sessionId,
              amount: null, // Will be set from backend response if needed
              productType: null, // Will be set from backend response if needed
              timestamp: new Date().toISOString()
            }));

            // Store affiliation code and message
            if (result.affiliationCode) {
              localStorage.setItem('contemplate_affiliation_code', result.affiliationCode);
              setRegistrationCode(result.affiliationCode);
            }
            if (result.message) {
              localStorage.setItem('contemplate_fulfill_message', result.message);
              setFulfillMessage(result.message);
            }

            // Show success state
            setRegistrationSuccess(true);

            // Track successful purchase
            if (window.gtag) {
              window.gtag('event', 'purchase', {
                transaction_id: sessionId,
                currency: 'USD',
              });
            }
            if (window.fbq) {
              window.fbq('track', 'Purchase', { currency: 'USD' });
            }
          }
        })
        .catch(error => {
          console.error('Stripe payment fulfillment error:', error);
          // Reset the ref on error so it can be retried if needed
          fulfillmentProcessedRef.current = false;
          // Still allow user to register manually
        });
      return;
    }

    // Check if user has completed payment (PayPal flow)
    const order = localStorage.getItem('contemplate_order');
    if (!order) {
      // Redirect to checkout if no order found
      navigate('/checkout');
      return;
    }

    // Check if affiliation code already exists from fulfill response
    const affiliationCode = localStorage.getItem('contemplate_affiliation_code');
    const message = localStorage.getItem('contemplate_fulfill_message');

    console.log('RegistrationPage - Checking for existing code:', {
      affiliationCode,
      message,
      order
    });

    if (affiliationCode) {
      // Payment was already fulfilled, show success state immediately
      console.log('Found existing affiliation code, showing success state:', affiliationCode);
      setRegistrationCode(affiliationCode);
      setRegistrationSuccess(true);
      if (message) {
        setFulfillMessage(message);
      }

      // Store registration info if available
      const orderData = JSON.parse(order);
      if (orderData.payerEmail) {
        setFormData(prev => ({ ...prev, email: orderData.payerEmail }));
      }
      localStorage.setItem('contemplate_registration', JSON.stringify({
        email: orderData.payerEmail || '',
        registrationCode: affiliationCode,
        registeredAt: new Date().toISOString()
      }));
    } else {
      // Pre-fill email from PayPal if available
      const orderData = JSON.parse(order);
      if (orderData.payerEmail) {
        setFormData(prev => ({ ...prev, email: orderData.payerEmail }));
      }
    }
  }, [navigate, searchParams]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const copyCode = () => {
    const codeToCopy = registrationCode || localStorage.getItem('contemplate_affiliation_code');
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const order = JSON.parse(localStorage.getItem('contemplate_order') || '{}');

      // Call the external PayPal fulfillment API
      const response = await registerUser(formData.name, formData.email, order);

      if (response.success) {
        // Track registration completion
        if (window.gtag) {
          window.gtag('event', 'sign_up', { method: 'email' });
        }
        if (window.fbq) {
          window.fbq('track', 'CompleteRegistration');
        }

        // Store registration info
        localStorage.setItem('contemplate_registration', JSON.stringify({
          name: formData.name,
          email: formData.email,
          registrationCode: response.affiliationCode,
          registeredAt: new Date().toISOString()
        }));

        // Show success state with code
        setRegistrationCode(response.affiliationCode);
        setRegistrationSuccess(true);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        submit: error.message || 'Registration failed. Please try again or contact support.'
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#efede6] flex items-center justify-center px-8 py-16">
      <div className="max-w-lg w-full">
        {/* Success Badge */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#202020] mb-6">
            <Check className="w-10 h-10 text-[#efede6]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-normal text-[#202020] mb-4 tracking-tight">
            Payment Successful!
          </h1>
          <p className="text-[#202020]/60 text-lg">
            Welcome to the Contemplate. Let's get you set up.
          </p>
        </div>

        {/* Positive Reinforcement */}
        <div className="bg-[#202020] rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0">
              <Heart className="w-5 h-5 text-[#efede6]" />
            </div>
            <div>
              <p className="text-[#efede6] font-medium mb-1">You've taken the first step</p>
              <p className="text-[#efede6]/60 text-sm">
                Most parents wish things were better. You're actually doing something about it.
                That takes courage.
              </p>
            </div>
          </div>
        </div>

        {registrationSuccess ? (
          /* Success State with Code */
          <>
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-medium text-[#202020] mb-2">Registration Successful!</h2>
                {fulfillMessage ? (
                  <p className="text-[#202020]/60 mb-2">{fulfillMessage}</p>
                ) : (
                  <p className="text-[#202020]/60">
                    Your registration code has been sent to <strong>{formData.email || 'your email'}</strong>
                  </p>
                )}
              </div>

              {/* Registration Code */}
              <div className="bg-[#efede6] rounded-2xl p-6 mb-6">
                <p className="text-[#202020]/50 text-sm mb-4 text-center">Your Registration Code{(registrationCode || localStorage.getItem('contemplate_affiliation_code'))?.includes(',') ? 's' : ''}</p>

                <div className="space-y-4">
                  {(registrationCode || localStorage.getItem('contemplate_affiliation_code'))?.split(',').map((code, index) => {
                    const trimmedCode = code.trim();
                    const hasMultiple = (registrationCode || localStorage.getItem('contemplate_affiliation_code'))?.includes(',');
                    return (
                      <div key={index} className="flex flex-col items-center gap-2">
                        {hasMultiple && (
                          <span className="text-xs text-[#202020]/40 uppercase tracking-widest">
                            {index === 0 ? 'Your Code' : 'Partner Code'}
                          </span>
                        )}
                        <div className="flex items-center justify-center gap-3 w-full">
                          <span className="text-3xl font-mono font-bold text-[#202020] tracking-wider bg-white/50 px-4 py-2 rounded-lg border border-[#202020]/5">
                            {trimmedCode}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(trimmedCode);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            className="p-3 bg-white rounded-lg hover:bg-[#202020]/5 transition-colors border border-[#202020]/5"
                            title="Copy code"
                          >
                            {copied ? (
                              <CheckCheck className="w-5 h-5 text-green-600" />
                            ) : (
                              <Copy className="w-5 h-5 text-[#202020]/40" />
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-[#202020] rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-medium text-[#efede6] mb-4">How to use your code:</h3>
                <div className="space-y-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] text-sm font-medium">1</div>
                    <p className="text-[#efede6] text-sm">Register on <strong>stage.contemplate.life</strong></p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] text-sm font-medium">2</div>
                    <p className="text-[#efede6] text-sm">Once registered, click on <strong>"Join using a Code"</strong> button</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] text-sm font-medium">3</div>
                    <p className="text-[#efede6] text-sm">Enter your code{(registrationCode || localStorage.getItem('contemplate_affiliation_code'))?.includes(',') ? 's' : ''} and submit to get access to the program</p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <Button
                onClick={() => window.location.href = 'https://stage.contemplate.life'}
                className="w-full bg-[#202020] hover:bg-[#303030] text-[#efede6] py-6 rounded-full text-base font-medium transition-all hover:scale-[1.02] group"
              >
                Go to stage.contemplate.life
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </>
        ) : (
          /* Registration Form */
          <>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-[#202020]/60" />
                <h2 className="text-xl font-medium text-[#202020]">Complete Your Registration</h2>
              </div>
              <p className="text-[#202020]/50 mb-8">
                Enter your details below and we'll send you your registration code to access the program.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#202020]/70 mb-2 block">Your Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`bg-[#efede6]/50 border-[#202020]/10 focus:border-[#202020]/30 h-12 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#202020]/70 mb-2 block">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`bg-[#efede6]/50 border-[#202020]/10 focus:border-[#202020]/30 h-12 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  <p className="text-[#202020]/40 text-xs mt-2">
                    We'll send your registration code and program access to this email.
                  </p>
                </div>

                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#202020] hover:bg-[#303030] text-[#efede6] py-6 rounded-full text-base font-medium transition-all hover:scale-[1.02] group"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-[#efede6]/30 border-t-[#efede6] rounded-full animate-spin" />
                      Registering...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Complete Registration
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>
              </form>
            </div>

            {/* What's Next */}
            <div className="mt-8 text-center">
              <p className="text-[#202020]/40 text-sm">
                After registration, you'll receive instant access to Week 1 of Contemplate.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RegistrationPage;
